import React from 'react';
import { StrategyPositionSnapshot } from '../../../types/trading';
import { BacktestRequest } from '../../order-ticket/strategy-backtest/config/types';
import { StrategyConfigView } from '../../order-ticket/strategy-backtest/components/StrategyConfigView';

/* ── Shared style tokens ──────────────────────────────────────────── */

const overlayS: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.62)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 2100, backdropFilter: 'blur(4px)', padding: '16px',
};

const dialogHeaderS: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: '12px', padding: '16px 18px 12px',
  borderBottom: '1px solid var(--config-border-muted, rgba(255,255,255,0.08))',
};

const titleS: React.CSSProperties = {
  margin: 0, fontSize: '1.02rem', fontWeight: 700,
  color: 'var(--config-text-primary, #e2e8f0)',
};

const bodyTextS: React.CSSProperties = {
  margin: '0 0 8px',
  color: 'var(--config-text-secondary, #ccc)',
  fontSize: '0.88rem', lineHeight: 1.4,
};

/* ── Strategy Close Confirmation Modal ────────────────────────────── */

interface StrategyCloseModalProps {
  target: StrategyPositionSnapshot;
  onDismiss: () => void;
  onConfirm: () => void;
  submitting: boolean;
  error: string | null;
}

export const StrategyCloseModal: React.FC<StrategyCloseModalProps> = ({
  target, onDismiss, onConfirm, submitting, error,
}) => (
  <div
    style={overlayS}
    onClick={(e) => { if (e.target === e.currentTarget) onDismiss(); }}
  >
    <div
      style={{
        width: 'min(500px, 100%)',
        background: 'var(--config-bg-secondary, #111)',
        border: '1px solid var(--config-border-muted, rgba(255,255,255,0.08))',
        borderRadius: '14px', overflow: 'hidden',
        boxShadow: '0 24px 56px rgba(0,0,0,0.36)',
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="strategy-close-title"
    >
      <div style={dialogHeaderS}>
        <h3 id="strategy-close-title" style={titleS}>Close Strategy</h3>
      </div>
      <div style={{ padding: '14px 18px' }}>
        <p style={bodyTextS}>
          Closing will remove <strong>{target.name}</strong> from the Strategy Position table.
        </p>
        <p style={bodyTextS}>
          This action does not liquidate positions in this iteration.
        </p>
        {error && (
          <div style={{ marginTop: '10px', color: 'var(--config-error, #ef4444)', fontSize: '0.85rem' }}>
            {error}
          </div>
        )}
      </div>
      <div style={{ padding: '0 18px 16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        <button
          type="button"
          style={{
            border: 'none', borderRadius: '8px', padding: '9px 13px',
            fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
            background: 'var(--config-bg-tertiary, rgba(255,255,255,0.06))',
            color: 'var(--config-text-primary, #e2e8f0)',
          }}
          onClick={onDismiss}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          style={{
            border: 'none', borderRadius: '8px', padding: '9px 13px',
            fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
            background: '#ef4444', color: '#fff',
          }}
          onClick={onConfirm}
          disabled={submitting}
        >
          {submitting ? 'Closing\u2026' : 'Confirm Close'}
        </button>
      </div>
    </div>
  </div>
);

/* ── Strategy View Configuration Modal ────────────────────────────── */

interface StrategyViewModalProps {
  target: StrategyPositionSnapshot;
  config: BacktestRequest | null;
  onClose: () => void;
}

export const StrategyViewModal: React.FC<StrategyViewModalProps> = ({
  target, config, onClose,
}) => (
  <div
    style={overlayS}
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
    <div
      style={{
        width: 'min(920px, 100%)', maxHeight: '86vh',
        background: 'var(--config-bg-secondary, #111)',
        border: '1px solid var(--config-border-muted, rgba(255,255,255,0.08))',
        borderRadius: '14px', overflow: 'hidden',
        boxShadow: '0 24px 56px rgba(0,0,0,0.36)',
        display: 'flex', flexDirection: 'column',
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="strategy-config-title"
    >
      <div style={dialogHeaderS}>
        <h3 id="strategy-config-title" style={titleS}>
          {target.name} Configuration
        </h3>
        <button
          type="button"
          style={{
            border: 'none', borderRadius: '6px', background: 'transparent',
            color: 'var(--config-text-muted, #888)', width: '30px', height: '30px',
            fontSize: '1.25rem', lineHeight: 1, cursor: 'pointer',
          }}
          onClick={onClose}
          aria-label="Close strategy configuration modal"
          title="Close"
        >
          &times;
        </button>
      </div>
      <div style={{ padding: '14px 18px', overflow: 'auto' }}>
        <StrategyConfigView config={config} />
      </div>
    </div>
  </div>
);
